import { useState } from 'react';
import {
  changePersonalIncome,
  describePAApiError,
  INCOME_CATEGORIES,
  INCOME_CATEGORY_LABEL,
  type IncomeCategory,
  type MoneyMonth,
  type PersonalBillRecurrence,
  type PersonalIncome,
} from '../../../lib/api/team.js';
import { hapticSuccess, hapticTap, hapticWarn } from '../../../lib/native.js';

/**
 * Ganhos no Dinheiro: o mês em três números (entrou, saiu, sobra prevista), o que
 * está para cair e o que já entrou, com categoria e um rótulo livre da pessoa
 * (CDB, Cofrinho, Guardado...). Só informação: nada de banco nem conselho.
 */

export function money(cents: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cents / 100);
}

/** "4.500", "4500", "129,90" e "129.90" viram centavos; vazio ou inválido vira 0. */
export function parseCents(text: string): number {
  const raw = text.replace(/[^\d.,]/g, '');
  if (!raw) return 0;
  const comma = /^(.*),(\d{1,2})$/.exec(raw);
  const dot = /^([\d]*)\.(\d{2})$/.exec(raw);
  const [whole, fraction] = comma
    ? [comma[1]!, comma[2]!]
    : dot && !raw.includes(',')
      ? [dot[1]!, dot[2]!]
      : [raw, ''];
  const reais = Number(whole.replace(/[.,]/g, '') || '0');
  const cents = Number(fraction.padEnd(2, '0') || '0');
  return Number.isSafeInteger(reais) ? reais * 100 + cents : 0;
}

const editable = (cents: number) =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2 }).format(cents / 100);

function today(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short', timeZone: 'UTC' })
    .format(new Date(`${value}T12:00:00Z`))
    .replace('.', '');
}

function whenCopy(item: PersonalIncome): { label: string; tone: string } {
  const days = Math.round(
    (Date.parse(`${item.date}T12:00:00Z`) - Date.parse(`${today()}T12:00:00Z`)) / 86_400_000,
  );
  if (days < 0) return { label: `Era para ${dateLabel(item.date)}`, tone: 'late' };
  if (days === 0) return { label: 'Deve cair hoje', tone: 'today' };
  if (days === 1) return { label: 'Deve cair amanhã', tone: 'soon' };
  return { label: `Deve cair ${dateLabel(item.date)}`, tone: 'calm' };
}

// Ícone da categoria, em traço (herda a cor de `pgs-ic`).
export function IncomeGlyph({ category, size = 22 }: { category: IncomeCategory; size?: number }) {
  return (
    <svg
      className="cnt-glyph"
      style={{ width: size, height: size }}
      viewBox="0 0 40 40"
      aria-hidden="true"
    >
      {category === 'salario' && (
        <>
          <rect x="6" y="12" width="28" height="20" rx="4" />
          <path d="M15 12V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3M6 21h28" />
        </>
      )}
      {category === 'freela' && (
        <>
          <rect x="7" y="9" width="26" height="17" rx="3" />
          <path d="M4 31h32M17 31l1-5h4l1 5" />
        </>
      )}
      {category === 'renda_extra' && (
        <>
          <circle cx="20" cy="20" r="13" />
          <path d="M20 13v14M13 20h14" />
        </>
      )}
      {category === 'rendimentos' && (
        <>
          <path d="M6 32h28" />
          <path d="m8 26 8-8 6 5 11-12" />
          <path d="M26 11h7v7" />
        </>
      )}
      {category === 'aluguel' && (
        <>
          <path d="m7 19 13-11 13 11v14H7Z" />
          <circle cx="20" cy="23" r="3.5" />
          <path d="M20 26.5V30" />
        </>
      )}
      {category === 'outros' && (
        <>
          <path d="M8 14h24v18H8Z" />
          <path d="M14 14V9h12v5M8 21h24" />
        </>
      )}
    </svg>
  );
}

/** Entrou, saiu e a sobra prevista do mês, sempre lidos do servidor (o app não soma). */
export function MoneyBand({ month }: { month: MoneyMonth }) {
  const negative = month.leftoverCents < 0;
  return (
    <div className="din-band" role="group" aria-label="O mês em números">
      <div className="din-cell">
        <small>Entrou</small>
        <b>{money(month.receivedCents)}</b>
        {month.expectedCents > 0 && <em>+ {money(month.expectedCents)} para cair</em>}
      </div>
      <div className="din-cell">
        <small>Saiu</small>
        <b>{money(month.paidCents)}</b>
        {month.dueCents > 0 && <em>+ {money(month.dueCents)} a pagar</em>}
      </div>
      <div className={`din-cell lead${negative ? ' short' : ''}`}>
        <small>Sobra prevista</small>
        <b>{money(month.leftoverCents)}</b>
        <em>{negative ? 'Falta para fechar o mês' : 'Se tudo cair e for pago'}</em>
      </div>
    </div>
  );
}

export function IncomeForm({
  income,
  labels,
  onCancel,
  onSaved,
}: {
  income?: PersonalIncome | undefined;
  labels: string[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const editing = Boolean(income);
  const [title, setTitle] = useState(income?.title ?? '');
  const [amount, setAmount] = useState(income ? editable(income.amountCents) : '');
  const [date, setDate] = useState(income?.date ?? today());
  const [received, setReceived] = useState(true);
  const [recurrence, setRecurrence] = useState<PersonalBillRecurrence>(
    income?.recurrence ?? 'none',
  );
  const [category, setCategory] = useState<IncomeCategory>(income?.category ?? 'salario');
  const [label, setLabel] = useState(income?.label ?? '');
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const amountCents = parseCents(amount);
  const choices = [...new Set([...labels, ...(income?.label ? [income.label] : [])])].slice(0, 8);

  async function run(work: () => Promise<unknown>) {
    setSaving(true);
    setError('');
    try {
      await work();
      hapticSuccess();
      await onSaved();
    } catch (err) {
      setError(describePAApiError(err, 'Não consegui guardar agora. Tenta mais uma vez?'));
      void hapticWarn();
      setSaving(false);
    }
  }

  function save() {
    if (title.trim().length < 2 || amountCents < 1 || !date) {
      setError('Preencha nome, valor e data para continuar.');
      void hapticWarn();
      return;
    }
    void run(() =>
      income
        ? changePersonalIncome({
            type: 'update',
            id: income.id,
            title: title.trim(),
            amountCents,
            date,
            recurrence,
            category,
            label: label.trim(),
          })
        : changePersonalIncome({
            type: 'add',
            title: title.trim(),
            amountCents,
            date,
            recurrence,
            received,
            category,
            ...(label.trim() ? { label: label.trim() } : {}),
          }),
    );
  }

  return (
    <section className="pgs-section cnt-form din-form">
      <h2 className="pgs-h2">{editing ? 'Mudar este ganho' : 'Novo ganho'}</h2>
      <label className="cnt-field">
        <span>De onde vem</span>
        <span className="cnt-input">
          <IncomeGlyph category={category} />
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Ex.: Salário, Freela do site"
            maxLength={80}
          />
        </span>
      </label>
      <div className="cnt-pair">
        <label className="cnt-field">
          <span>Valor</span>
          <span className="cnt-input">
            <b>R$</b>
            <input
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              inputMode="decimal"
              placeholder="4.500,00"
            />
          </span>
        </label>
        <label className="cnt-field">
          <span>
            {(income ? income.status === 'received' : received) ? 'Quando caiu' : 'Quando cai'}
          </span>
          <span className="cnt-input">
            <input value={date} onChange={(event) => setDate(event.target.value)} type="date" />
          </span>
        </label>
      </div>
      {!editing && (
        <div className="pgs-chips" role="group" aria-label="Já entrou?">
          <button
            type="button"
            className={`pgs-chip${received ? ' on' : ''}`}
            aria-pressed={received}
            onClick={() => setReceived(true)}
          >
            Já caiu
          </button>
          <button
            type="button"
            className={`pgs-chip${!received ? ' on' : ''}`}
            aria-pressed={!received}
            onClick={() => setReceived(false)}
          >
            Ainda vai cair
          </button>
        </div>
      )}
      <span className="cnt-label">Categoria</span>
      <div className="pgs-chips" role="group" aria-label="Categoria">
        {INCOME_CATEGORIES.map((key) => (
          <button
            key={key}
            type="button"
            className={`pgs-chip${category === key ? ' on' : ''}`}
            aria-pressed={category === key}
            onClick={() => setCategory(key)}
          >
            {INCOME_CATEGORY_LABEL[key]}
          </button>
        ))}
      </div>
      <label className="cnt-field">
        <span>Rótulo seu (opcional)</span>
        <span className="cnt-input">
          <input
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder="Ex.: CDB, Cofrinho, Guardado"
            maxLength={30}
          />
        </span>
      </label>
      {choices.length > 0 && (
        <div className="pgs-chips" role="group" aria-label="Rótulos que você já usa">
          {choices.map((item) => (
            <button
              key={item}
              type="button"
              className={`pgs-chip${label.trim() === item ? ' on' : ''}`}
              aria-pressed={label.trim() === item}
              onClick={() => setLabel(label.trim() === item ? '' : item)}
            >
              {item}
            </button>
          ))}
        </div>
      )}
      <span className="cnt-label">Ele se repete?</span>
      <div className="pgs-chips" role="group" aria-label="Ele se repete?">
        <button
          type="button"
          className={`pgs-chip${recurrence === 'monthly' ? ' on' : ''}`}
          aria-pressed={recurrence === 'monthly'}
          onClick={() => setRecurrence('monthly')}
        >
          Todo mês
        </button>
        <button
          type="button"
          className={`pgs-chip${recurrence === 'none' ? ' on' : ''}`}
          aria-pressed={recurrence === 'none'}
          onClick={() => setRecurrence('none')}
        >
          Só desta vez
        </button>
      </div>
      <p className="cnt-hint">
        {recurrence === 'monthly'
          ? 'Quando cair, eu já deixo o do mês que vem previsto, no mesmo dia.'
          : 'Um ganho só.'}
      </p>
      {error && (
        <p className="cnt-error" role="alert">
          {error}
        </p>
      )}
      <div className="pgs-actions">
        <button type="button" className="pgs-btn" onClick={onCancel}>
          {editing ? 'Fechar' : 'Agora não'}
        </button>
        <button type="button" className="pgs-btn solid" disabled={saving} onClick={save}>
          {saving ? 'Guardando…' : editing ? 'Guardar' : 'Guardar ganho'}
        </button>
      </div>
      {income && (
        <button
          type="button"
          className={`pgs-btn small din-drop${confirming ? ' sure' : ''}`}
          disabled={saving}
          onClick={() =>
            confirming
              ? void run(() => changePersonalIncome({ type: 'delete', id: income.id }))
              : setConfirming(true)
          }
        >
          {confirming ? 'Apagar mesmo' : 'Apagar este ganho'}
        </button>
      )}
    </section>
  );
}

/** O que está para cair e o que já entrou neste mês, com a ação de cada um. */
export function IncomesSection({
  incomes,
  labels,
  month,
  onChanged,
  setAdding,
}: {
  incomes: PersonalIncome[];
  labels: string[];
  month: string | null;
  onChanged: () => Promise<void>;
  /** Abrir um ganho para mudar fecha o formulário de ganho novo. */
  setAdding: (value: boolean) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState('');
  const [justIn, setJustIn] = useState<string | null>(null);
  const limit = new Date(Date.now() + 45 * 86_400_000).toISOString().slice(0, 10);
  const expected = incomes
    .filter((item) => item.status === 'expected' && item.date <= limit)
    .sort((a, b) => a.date.localeCompare(b.date));
  const received = incomes
    .filter((item) => item.status === 'received' && (!month || item.date.startsWith(month)))
    .sort((a, b) => b.date.localeCompare(a.date));

  async function act(item: PersonalIncome, type: 'receive' | 'skip' | 'reopen') {
    setBusy(item.id);
    setFailed('');
    try {
      await changePersonalIncome({ type, id: item.id });
      if (type === 'receive') {
        setJustIn(item.id);
        hapticSuccess();
        setTimeout(() => setJustIn(null), 1200);
      } else void hapticTap();
      await onChanged();
    } catch (err) {
      setFailed(describePAApiError(err, 'Não consegui mudar esse ganho agora. Tente de novo.'));
      void hapticWarn();
    } finally {
      setBusy(null);
    }
  }

  const closeForm = () => {
    setEditing(null);
    setAdding(false);
  };
  const saved = async () => {
    closeForm();
    await onChanged();
  };
  const same = (a: string, b: string) =>
    a.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase() ===
    b.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const tagLine = (item: PersonalIncome) =>
    [
      !same(item.title, INCOME_CATEGORY_LABEL[item.category]) &&
        INCOME_CATEGORY_LABEL[item.category],
      item.label,
      item.recurrence === 'monthly' && 'todo mês',
    ]
      .filter(Boolean)
      .join(' · ');

  const row = (item: PersonalIncome, kind: 'expected' | 'received') => {
    if (editing === item.id)
      return (
        <IncomeForm
          key={item.id}
          income={item}
          labels={labels}
          onCancel={closeForm}
          onSaved={saved}
        />
      );
    if (kind === 'received')
      return (
        <div key={item.id} className="pgs-row din-income din-in">
          <span className="pgs-ic din-ic">
            <IncomeGlyph category={item.category} />
          </span>
          <button
            type="button"
            className="pgs-rowt din-open"
            onClick={() => {
              setEditing(item.id);
              setAdding(false);
            }}
            aria-label={`Ver ${item.title}`}
          >
            <b>{item.title}</b>
            <small>
              <strong className="din-in-val">{money(item.amountCents)}</strong> ·{' '}
              {[dateLabel(item.date), tagLine(item)].filter(Boolean).join(' · ')}
            </small>
          </button>
          <button
            type="button"
            className="pgs-btn small"
            disabled={busy === item.id}
            onClick={() => void act(item, 'reopen')}
          >
            Desfazer
          </button>
          <button
            type="button"
            className="pgs-btn small cnt-edit"
            disabled={busy === item.id}
            onClick={() => {
              setEditing(item.id);
              setAdding(false);
              void hapticTap();
            }}
            aria-label={`Mudar ${item.title}`}
          >
            Mudar
          </button>
        </div>
      );
    const when = whenCopy(item);
    return (
      <div
        key={item.id}
        className={`pgs-row cnt-bill din-income${justIn === item.id ? ' paid-now' : ''}`}
      >
        <span className="pgs-ic din-ic">
          <IncomeGlyph category={item.category} />
        </span>
        <button
          type="button"
          className="pgs-rowt din-open"
          onClick={() => {
            setEditing(item.id);
            setAdding(false);
          }}
          aria-label={`Ver ${item.title}`}
        >
          <b>{item.title}</b>
          <small>
            <em className={`cnt-due ${when.tone}`}>{when.label}</em>
            {tagLine(item) && ` · ${tagLine(item)}`}
          </small>
        </button>
        <b className="pgs-val din-val">{money(item.amountCents)}</b>
        <div className="cnt-acts">
          <button
            type="button"
            className="pgs-btn small"
            disabled={busy === item.id}
            onClick={() => void act(item, 'receive')}
          >
            Caiu
          </button>
          <button
            type="button"
            className="pgs-btn small cnt-edit"
            disabled={busy === item.id}
            onClick={() => {
              setEditing(item.id);
              setAdding(false);
              void hapticTap();
            }}
            aria-label={`Mudar ${item.title}`}
          >
            Mudar
          </button>
          <button
            type="button"
            className="pgs-btn small cnt-drop"
            disabled={busy === item.id}
            onClick={() => void act(item, 'skip')}
          >
            Não veio
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      {failed && (
        <p className="cnt-error" role="alert">
          {failed}
        </p>
      )}
      {expected.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Para cair <small>{expected.length}</small>
          </h2>
          {expected.map((item) => row(item, 'expected'))}
        </section>
      )}
      {received.length > 0 && (
        <section className="pgs-section">
          <h2 className="pgs-h2">
            Entrou este mês <small>{money(received.reduce((s, i) => s + i.amountCents, 0))}</small>
          </h2>
          {received.map((item) => row(item, 'received'))}
        </section>
      )}
    </>
  );
}
